import React, { Suspense, lazy, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { S } from "@/pages/ibloom/styles";

const MunimJiPanel = lazy(() => import("@/pages/ibloom/mapbazzar/MunimJiPanel"));

const STEPS = [
  { icon: "📸", t: "Snap a photo", s: "Point your camera at any product on your shelf." },
  { icon: "✨", t: "AI writes the listing", s: "ShopKeeper AI reads the pack, writes the title, description & a fair price, and makes an Amazon-style photo." },
  { icon: "📍", t: "Goes live instantly", s: "Your product appears on the Mapbazar map — nearby shoppers can find and buy it." },
];

const FEATURES = [
  { icon: "🆓", t: "Free to use", s: "Works on any phone. No typing, no editing." },
  { icon: "🏬", t: "Your own storefront", s: "Every item you add builds your live local shop." },
  { icon: "💬", t: "Buyers reach you", s: "Customers can chat you on WhatsApp and place orders." },
];

export default function AiShelf() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [munimOpen, setMunimOpen] = useState(false);

  const showToast = (m) => toast(m);
  const requireAuth = (reason) => {
    if (user) return true;
    toast(reason || "Please sign in to continue.");
    navigate("/login");
    return false;
  };

  const openUpload = () => {
    if (!requireAuth("Sign in to upload products with ShopKeeper AI.")) return;
    setMunimOpen(true);
  };

  return (
    <div data-testid="ai-shelf-page" style={{ minHeight: "100dvh", background: "#FAF9F6" }}>
      {/* Header */}
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(250,249,246,0.92)", backdropFilter: "blur(10px)", borderBottom: "1px solid #EAE8DC", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
        <button data-testid="ai-shelf-back-btn" onClick={() => navigate(-1)} style={{ width: 34, height: 34, borderRadius: "50%", border: "1px solid #EAE8DC", background: "white", fontSize: 16, cursor: "pointer", color: "#0F3D2E", flexShrink: 0 }}>‹</button>
        <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 800, color: "#0F3D2E" }} data-no-translate>Ai Shelf</div>
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "20px 16px 120px" }}>
        {/* Hero */}
        <div data-testid="ai-shelf-hero" style={{ background: "linear-gradient(135deg, #0F3D2E, #1C6B4A)", borderRadius: 22, padding: "26px 22px", color: "white", boxShadow: "0 14px 36px rgba(15,61,46,0.28)" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.16)", borderRadius: 100, padding: "5px 12px", fontSize: 11, fontWeight: 800, letterSpacing: 0.4 }} data-no-translate>✨ AI-Powered</div>
          <h1 style={{ ...S.fraunces, fontSize: 26, fontWeight: 800, lineHeight: 1.2, margin: "14px 0 8px" }}>
            Bring your offline shelf to a live online store.
          </h1>
          <p style={{ fontSize: 13.5, opacity: 0.9, lineHeight: 1.55, margin: 0 }}>
            Just point your camera at a product. ShopKeeper AI turns your physical shelf into a live digital storefront — no typing, no editing.
          </p>
          <button data-testid="ai-shelf-upload-cta" onClick={openUpload} style={{ marginTop: 20, width: "100%", padding: 15, borderRadius: 100, border: "none", background: "white", color: "#0F3D2E", fontSize: 15, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: "0 8px 22px rgba(0,0,0,0.18)" }}>
            📸 Upload a product with AI
          </button>
          <button data-testid="ai-shelf-scan-cta" onClick={() => { if (!requireAuth("Sign in to scan your shelf.")) return; navigate("/shelf-scan"); }} style={{ marginTop: 10, width: "100%", padding: 14, borderRadius: 100, border: "1.5px solid rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.12)", color: "white", fontSize: 14, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            🔍 NEW · Scan your whole shelf at once
          </button>
        </div>

        {/* How it works */}
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: "#8A94A6", marginBottom: 12 }} data-no-translate>HOW IT WORKS</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {STEPS.map((x, i) => (
              <div key={i} data-testid={`ai-shelf-step-${i}`} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "white", border: "1px solid #EAE8DC", borderRadius: 16, padding: 16, boxShadow: "0 3px 10px rgba(15,20,30,0.05)" }}>
                <span style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 14, background: "#0F3D2E12", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{x.icon}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 800, color: "#1A1A1A" }}>{i + 1}. {x.t}</div>
                  <div style={{ fontSize: 12.5, color: "#666", lineHeight: 1.5, marginTop: 3 }}>{x.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {FEATURES.map((x, i) => (
            <div key={i} data-testid={`ai-shelf-feature-${i}`} style={{ background: "white", border: "1px solid #EAE8DC", borderRadius: 14, padding: 14, textAlign: "center", boxShadow: "0 3px 10px rgba(15,20,30,0.05)" }}>
              <div style={{ fontSize: 24 }}>{x.icon}</div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: "#1A1A1A", marginTop: 6 }}>{x.t}</div>
              <div style={{ fontSize: 10.5, color: "#777", lineHeight: 1.4, marginTop: 3 }}>{x.s}</div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <button data-testid="ai-shelf-upload-cta-bottom" onClick={openUpload} style={{ marginTop: 26, width: "100%", padding: 16, borderRadius: 100, border: "none", background: "linear-gradient(135deg, #C97B3C, #8B1A4A)", color: "white", fontSize: 15.5, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: "0 10px 28px rgba(139,26,74,0.35)" }}>
          ✨ Start uploading — one tap
        </button>
      </div>

      {munimOpen && (
        <Suspense fallback={null}>
          <MunimJiPanel
            onClose={() => setMunimOpen(false)}
            requireAuth={requireAuth}
            showToast={showToast}
            onPublished={() => {}}
          />
        </Suspense>
      )}
    </div>
  );
}
